import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Auditoria } from "../../../interfaces";
import { formatarDataBrasilia } from "../../../utils/formatDate";
import { alpha } from "@mui/material/styles";

interface Modificacao {
  key: string;
  oldValue: unknown;
  newValue: unknown;
}

interface TabelaModificacoesProps {
  changes: Modificacao[];
  formatValue: (key: string, value: any) => React.ReactNode;
}

function semValor(value: unknown) {
  return value === null || value === undefined || value === "";
}

function ValorModificado({
  campo,
  valor,
  formatValue,
}: {
  campo: string;
  valor: unknown;
  formatValue: TabelaModificacoesProps["formatValue"];
}) {
  if (semValor(valor)) {
    return (
      <Box component="span" sx={{ color: "text.secondary" }} aria-label="Sem valor">
        —
      </Box>
    );
  }

  if (typeof valor === "string" && /^https?:\/\/\S+$/i.test(valor)) {
    try {
      const url = new URL(valor);
      return (
        <Link
          href={url.href}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          sx={(theme) => {
            const color = theme.palette.mode === "dark" ? "#90caf9" : theme.palette.primary.main;
            return {
              color,
              textDecorationColor: alpha(color, 0.45),
              textUnderlineOffset: "3px",
              "&:visited": { color },
              "&:hover": { textDecorationColor: color },
              "&:focus-visible": {
                outline: "2px solid currentColor",
                outlineOffset: 2,
                borderRadius: 0.5,
              },
            };
          }}
        >
          {valor}
        </Link>
      );
    } catch {
      // Valores que não são URLs válidas continuam visíveis como texto.
    }
  }

  return <>{formatValue(campo, valor)}</>;
}

export function TabelaModificacoes({ changes, formatValue }: TabelaModificacoesProps) {
  const temAntigo = changes.some((change) => !semValor(change.oldValue));
  const temNovo = changes.some((change) => !semValor(change.newValue));
  const larguraAntigo = temAntigo && !temNovo ? "58%" : !temAntigo && temNovo ? "18%" : "38%";
  const larguraNovo = temNovo && !temAntigo ? "58%" : !temNovo && temAntigo ? "18%" : "38%";

  if (!changes.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Nenhuma modificação encontrada.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, maxWidth: "100%" }}>
      <Table
        size="small"
        aria-label="Comparação dos valores modificados"
        sx={{
          tableLayout: "fixed",
          minWidth: 480,
          "& th, & td": {
            px: 1.5,
            py: 0.875,
            fontSize: 13,
            lineHeight: 1.5,
            fontVariantNumeric: "tabular-nums",
            verticalAlign: "top",
            textAlign: "left",
            overflowWrap: "anywhere",
          },
          "& thead th": { fontSize: 12, fontWeight: 600, py: 1.25, bgcolor: "action.hover" },
          "& tr > :not(:last-child)": { borderRight: 1, borderRightColor: "divider" },
          "& tbody tr:last-child > *": { borderBottom: 0 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "24%" }}>Campo</TableCell>
            <TableCell sx={{ width: larguraAntigo, color: "text.secondary" }}>Antigo</TableCell>
            <TableCell sx={{ width: larguraNovo }}>Novo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((change) => (
            <TableRow key={change.key} hover>
              <TableCell
                component="th"
                scope="row"
                sx={{
                  fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
                  color: "text.secondary",
                }}
              >
                {change.key}
              </TableCell>
              <TableCell sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
                <ValorModificado campo={change.key} valor={change.oldValue} formatValue={formatValue} />
              </TableCell>
              <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                <ValorModificado campo={change.key} valor={change.newValue} formatValue={formatValue} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface DialogModificacoesProps extends TabelaModificacoesProps {
  open: boolean;
  onClose: () => void;
  row: Auditoria;
  idRegistro: string | number;
}

export function DialogModificacoes({ open, onClose, row, idRegistro, changes, formatValue }: DialogModificacoesProps) {
  const titleId = React.useId();
  const metadados = [
    { label: "Id auditoria", value: row.id },
    { label: "Id", value: idRegistro },
    { label: "Camada", value: row.camada },
    { label: "Usuário", value: row.usuario },
    { label: "Data", value: formatarDataBrasilia(row.data) },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby={titleId}
      PaperProps={{
        elevation: 1,
        sx: {
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          colorScheme: (theme) => theme.palette.mode,
        },
      }}
    >
      <DialogTitle id={titleId} sx={{ fontSize: 19, fontWeight: 600, py: 2, borderBottom: 1, borderColor: "divider" }}>
        Modificações
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pb: 2,
          scrollbarWidth: "thin",
          scrollbarColor: (theme) => `${alpha(theme.palette.text.primary, 0.24)} transparent`,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2.5, rowGap: 0.75, py: 2 }}>
          {metadados.map(({ label, value }) => (
            <Typography
              key={label}
              component="div"
              variant="body2"
              sx={{
                overflowWrap: "anywhere",
                fontSize: 12,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
                {label}:
              </Box>{" "}
              {value ?? "—"}
            </Typography>
          ))}
        </Box>
        <TabelaModificacoes changes={changes} formatValue={formatValue} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="success"
          disableElevation
          size="small"
          sx={{ minWidth: 80, textTransform: "none" }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
